import { ReactElement, useEffect, useRef } from "react";
import { MapboxOptions, EventData } from "mapbox-gl";
import { MapEvent } from "../../../services/map/interfaces/map-event";
import { MapService } from "../../../services/map/MapService";
import { useDependency } from "../../../contexts/DependencyContext";
import ThemeSelector from "./ThemeSelector";
import styles from "./MapBase.module.scss";

interface MapBaseProps
{
    config?: MapboxOptions;
    withThemeSelector?: boolean;
    onError?: (e?: EventData) => void;
    onLoad?: (e?: EventData) => void;
    onIdle?: (e?: EventData) => void;
    onRemove?: (e?: EventData) => void;
    onRender?: (e?: EventData) => void;
    onResize?: (e?: EventData) => void;
    onWebGlContextLost?: (e?: EventData) => void;
    onWebGlContextRestored?: (e?: EventData) => void;
    onDataLoading?: (e?: EventData) => void;
    onData?: (e?: EventData) => void;
    onTileDataLoading?: (e?: EventData) => void;
    onSourceDataLoading?: (e?: EventData) => void;
    onStyleDataLoading?: (e?: EventData) => void;
    onSourceData?: (e?: EventData) => void;
    onStyleData?: (e?: EventData) => void;
    onBoxZoomCancel?: (e?: EventData) => void;
    onBoxZoomStart?: (e?: EventData) => void;
    onBoxZoomEnd?: (e?: EventData) => void;
    onTouchCancel?: (e?: EventData) => void;
    onTouchMove?: (e?: EventData) => void;
    onTouchEnd?: (e?: EventData) => void;
    onTouchStart?: (e?: EventData) => void;
    onClick?: (e?: EventData) => void;
    onContextMenu?: (e?: EventData) => void;
    onDblClick?: (e?: EventData) => void;
    onMouseMove?: (e?: EventData) => void;
    onMouseUp?: (e?: EventData) => void;
    onMouseDown?: (e?: EventData) => void;
    onMouseOut?: (e?: EventData) => void;
    onMouseOver?: (e?: EventData) => void;
    onMoveStart?: (e?: EventData) => void;
    onMove?: (e?: EventData) => void;
    onMoveEnd?: (e?: EventData) => void;
    onZoomStart?: (e?: EventData) => void;
    onZoom?: (e?: EventData) => void;
    onZoomEnd?: (e?: EventData) => void;
    onRotateStart?: (e?: EventData) => void;
    onRotate?: (e?: EventData) => void;
    onRotateEnd?: (e?: EventData) => void;
    onDragStart?: (e?: EventData) => void;
    onDrag?: (e?: EventData) => void;
    onDragEnd?: (e?: EventData) => void;
    onPitchStart?: (e?: EventData) => void;
    onPitch?: (e?: EventData) => void;
    onPitchEnd?: (e?: EventData) => void;
    onWheel?: (e?: EventData) => void;
}

const MapBase = (props: MapBaseProps): ReactElement =>
{
    const mapService = useDependency(MapService);
    const config = useRef(props.config);
    const events: MapEvent[] = [
        { name: "error", callback: props.onError },
        { name: "load", callback: props.onLoad },
        { name: "idle", callback: props.onIdle },
        { name: "remove", callback: props.onRemove },
        { name: "render", callback: props.onRender },
        { name: "resize", callback: props.onResize },
        { name: "webglcontextlost", callback: props.onWebGlContextLost },
        { name: "webglcontextrestored", callback: props.onWebGlContextRestored },
        { name: "dataloading", callback: props.onDataLoading },
        { name: "data", callback: props.onData },
        { name: "tiledataloading", callback: props.onTileDataLoading },
        { name: "sourcedataloading", callback: props.onSourceDataLoading },
        { name: "styledataloading", callback: props.onStyleDataLoading },
        { name: "sourcedata", callback: props.onSourceData },
        { name: "styledata", callback: props.onStyleData },
        { name: "boxzoomcancel", callback: props.onBoxZoomCancel },
        { name: "boxzoomstart", callback: props.onBoxZoomStart },
        { name: "boxzoomend", callback: props.onBoxZoomEnd },
        { name: "touchcancel", callback: props.onTouchCancel },
        { name: "touchmove", callback: props.onTouchMove },
        { name: "touchend", callback: props.onTouchEnd },
        { name: "touchstart", callback: props.onTouchStart },
        { name: "click", callback: props.onClick },
        { name: "contextmenu", callback: props.onContextMenu },
        { name: "dblclick", callback: props.onDblClick },
        { name: "mousemove", callback: props.onMouseMove },
        { name: "mouseup", callback: props.onMouseUp },
        { name: "mousedown", callback: props.onMouseDown },
        { name: "mouseout", callback: props.onMouseOut },
        { name: "mouseover", callback: props.onMouseOver },
        { name: "movestart", callback: props.onMoveStart },
        { name: "move", callback: props.onMove },
        { name: "moveend", callback: props.onMoveEnd },
        { name: "zoomstart", callback: props.onZoomStart },
        { name: "zoom", callback: props.onZoom },
        { name: "zoomend", callback: props.onZoomEnd },
        { name: "rotatestart", callback: props.onRotateStart },
        { name: "rotate", callback: props.onRotate },
        { name: "rotateend", callback: props.onRotateEnd },
        { name: "dragstart", callback: props.onDragStart },
        { name: "drag", callback: props.onDrag },
        { name: "dragend", callback: props.onDragEnd },
        { name: "pitchstart", callback: props.onPitchStart },
        { name: "pitch", callback: props.onPitch },
        { name: "pitchend", callback: props.onPitchEnd },
        { name: "wheel", callback: props.onWheel }
    ].filter(x => x.callback);

    const selectTheme = (e: Event): void =>
    {
        const target = e.currentTarget as HTMLButtonElement;
        mapService.setTheme(target.value);
    };

    useEffect(() => {
        mapService.setMap(config.current);
        return () => mapService.destroyMap();
    }, [mapService]);

    useEffect(() =>
    {
        if (events.length)
            events.forEach((event: EventData) => mapService.map.on(event.name, event.callback));

        return () =>
        {
            if (events.length)
                events.forEach((event: EventData) => mapService.map.off(event.name, event.callback));
        }
    }, [mapService, events]);

    return (
        <div className={styles.mapWrapper}>
            <div id="map" className={styles.map} />
            {props.withThemeSelector && <ThemeSelector {...{ selectTheme }} />}
        </div>
    );
};

export default MapBase;
